import { getAccessToken, refreshToken, isTokenExpired, getCsrfToken } from '../services/authService';
import securityConfig from '../config/securityConfig';

// Flag to prevent multiple refresh attempts simultaneously
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

// Function to add callbacks to the refresh subscribers
const subscribeToTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

// Function to notify all subscribers about the new token
const onRefreshSuccess = (token: string) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

/**
 * Makes an authenticated API request using the in-memory token
 */
export const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
  let token = getAccessToken();
  
  // Check if token is expired and try to refresh it
  if (token && isTokenExpired(token)) {
    // If already refreshing, wait for the new token
    if (isRefreshing) {
      token = await new Promise<string>((resolve, reject) => {
        subscribeToTokenRefresh((newToken: string) => {
          resolve(newToken);
        });
        
        // Add a timeout to prevent hanging indefinitely
        setTimeout(() => {
          reject(new Error('Token refresh timeout'));
        }, 10000);
      });
    } else {
      try {
        isRefreshing = true;
        token = await refreshToken();
        
        if (token) {
          // Notify all subscribers about the new token
          onRefreshSuccess(token);
        } else {
          throw new Error('Authentication token expired and refresh failed');
        }
      } finally {
        isRefreshing = false;
      }
    }
  }
  
  if (!token) {
    throw new Error('No authentication token available');
  }
  
  // Get CSRF token
  const csrfToken = getCsrfToken();
  
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };
  
  // Only set Content-Type if not set in options and we're not handling FormData
  // Check for FormData by seeing if body is a FormData instance
  const isFormData = options.body instanceof FormData;
  if (!isFormData && !options.headers?.['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  
  // Add CSRF token if available for non-GET requests
  if (csrfToken && options.method !== 'GET') {
    headers[securityConfig.csrfHeaderName] = csrfToken;
  }
  
  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include' // Include cookies
  });
  
  // If we get a 401, try to refresh the token and retry the request
  if (response.status === 401) {
    // Only attempt refresh if not already refreshing
    if (!isRefreshing) {
      try {
        isRefreshing = true;
        const newToken = await refreshToken();
        
        if (newToken) {
          // Notify all subscribers about the new token
          onRefreshSuccess(newToken);
          
          // Retry the request with the new token
          return fetch(url, {
            ...options,
            headers: {
              ...options.headers,
              'Authorization': `Bearer ${newToken}`,
              ...(csrfToken && options.method !== 'GET' ? { [securityConfig.csrfHeaderName]: getCsrfToken() || '' } : {})
            },
            credentials: 'include'
          });
        } else {
          window.dispatchEvent(new Event('authExpired'));
          throw new Error('Authentication failed and token refresh failed');
        }
      } finally {
        isRefreshing = false;
      }
    } else {
      // If already refreshing, wait for the new token
      const newToken = await new Promise<string>((resolve, reject) => {
        subscribeToTokenRefresh((token: string) => {
          resolve(token);
        });
        
        // Add a timeout to prevent hanging indefinitely
        setTimeout(() => {
          reject(new Error('Token refresh timeout'));
        }, 10000);
      });
      
      // Retry the request with the new token
      return fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${newToken}`,
          ...(csrfToken && options.method !== 'GET' ? { [securityConfig.csrfHeaderName]: getCsrfToken() || '' } : {})
        },
        credentials: 'include'
      });
    }
  }
  
  return response;
};

/**
 * Makes an authenticated GET request
 */
export const authenticatedGet = async (url: string) => {
  return authenticatedFetch(url, { method: 'GET' });
};

// Helper function to handle long URLs
export const shortenUrlForStorage = (url: string, maxLength = 250): string => {
  if (!url || url.length <= maxLength) return url;
  
  // For S3 URLs, we could store just the key part and reconstruct it later
  // Check if it's an S3 URL
  if (url.includes('s3.amazonaws.com')) {
    try {
      const urlObj = new URL(url);
      // Extract just the path part
      const path = urlObj.pathname;
      // If the path is still too long, truncate it
      if (path.length > maxLength) {
        return path.substring(0, maxLength);
      }
      return path;
    } catch (e) {
      console.error('Error parsing URL:', e);
      // Fallback to simple truncation
      return url.substring(0, maxLength);
    }
  }
  
  // For other URLs, simply truncate and add ellipsis
  return url.substring(0, maxLength - 3) + '...';
};

/**
 * Makes an authenticated POST request specifically for file uploads
 * This is separate to handle FormData properly
 */
export const authenticatedFileUpload = async (
  url: string,
  formData: FormData
): Promise<Response> => {
  const token = getAccessToken();
  
  if (!token) {
    console.error("authenticatedFileUpload: No token found");
    throw new Error('No authentication token found');
  }
  
  console.log(`File upload to ${url} initiated`);
  
  // For FormData, don't set Content-Type as the browser will set it with the boundary
  const headers: HeadersInit = {
    'Authorization': `Bearer ${token}`
  };
  
  // Add CSRF token if available
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    console.log("Adding CSRF token to file upload");
    headers[securityConfig.csrfHeaderName] = csrfToken;
  } else {
    console.warn("No CSRF token available for file upload");
  }
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include' // Include cookies
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`File upload failed: ${response.status} ${errorText}`);
    } else {
      console.log('File upload successful');
    }
    
    return response;
  } catch (error) {
    console.error('File upload error:', error);
    throw error;
  }
};

/**
 * Makes an authenticated POST request
 */
export const authenticatedPost = async (
  url: string,
  data: any,
  skipJsonContentType = false
): Promise<Response> => {
  // Special handling for profile picture uploads which use FormData
  if (skipJsonContentType && url.includes('/profiles/profile-picture')) {
    if (data instanceof FormData) {
      return authenticatedFileUpload(url, data);
    }
  }
  
  // For regular JSON requests, use authenticatedFetch
  const token = getAccessToken();
  
  if (!token) {
    console.error("authenticatedPost: No token found in localStorage");
    throw new Error('No authentication token found');
  }
  
  // Check if this is a profile picture upload
  const isProfilePicture = url.includes('/profiles/profile-picture');
  
  // Prepare options for authenticatedFetch
  const options: RequestInit = {
    method: 'POST',
    body: skipJsonContentType ? data : JSON.stringify(data),
  };
  
  if (!skipJsonContentType) {
    // For JSON requests, prepare the data
    if (data && typeof data === 'object' && data.profilePictureUrl) {
      data = {
        ...data,
        profilePictureUrl: shortenUrlForStorage(data.profilePictureUrl)
      };
    }
  }
  
  // Use authenticatedFetch to handle token refresh etc.
  const response = await authenticatedFetch(url, options);
  
  // For profile picture uploads, we need to modify the response
  if (isProfilePicture && response.ok) {
    try {
      const originalData = await response.clone().json();
      
      // If the response contains a profile picture URL that's too long, shorten it
      if (originalData && originalData.profilePictureUrl) {
        const shortenedUrl = shortenUrlForStorage(originalData.profilePictureUrl);
        
        // If we shortened the URL, create a new response
        if (shortenedUrl !== originalData.profilePictureUrl) {
          originalData.profilePictureUrl = shortenedUrl;
          originalData.originalProfilePictureUrl = originalData.profilePictureUrl;
          
          // Create a modified response
          const modifiedResponse = new Response(JSON.stringify(originalData), {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers
          });
          
          return modifiedResponse;
        }
      }
    } catch (error) {
      console.error("Error processing profile picture response:", error);
    }
  }
  
  return response;
};

/**
 * Makes an authenticated PUT request
 */
export const authenticatedPut = async (
  url: string,
  data: any
): Promise<Response> => {
  // Process data before sending
  let processedData = data;
  if (data && typeof data === 'object' && data.profilePictureUrl) {
    processedData = {
      ...data,
      profilePictureUrl: shortenUrlForStorage(data.profilePictureUrl)
    };
  }
  
  // Use authenticatedFetch helper to handle token refresh etc.
  return authenticatedFetch(url, {
    method: 'PUT',
    body: JSON.stringify(processedData)
  });
};

/**
 * Makes an authenticated DELETE request
 */
export const authenticatedDelete = async (url: string) => {
  return authenticatedFetch(url, { method: 'DELETE' });
}; 